import { useCallback } from "react";
import {
  cancelTurn,
  openRegenerateStream,
  openRunStream,
  openSendStream,
  renameConversation,
  type ChatCallError,
} from "@/features/intelligence/api/client";
import type { IntelligenceAttachmentRef } from "@/features/intelligence/api/shapes";
import type { AccessAnswer } from "@/features/intelligence/lib/access-probe";
import {
  beginRegenerate,
  beginTurn,
  type ThreadState,
  type TurnOutcome,
} from "@/features/intelligence/lib/thread-state";
import { titleFromFirstMessage } from "./runtime";
import type { ApplyThread, DriveStream, MutableRef, StateSetter } from "./types";

export function useChatActions({
  locale,
  activeIdRef,
  busyRef,
  threadRef,
  frontendContextRef,
  ensureConversation,
  hasUploadsInFlight,
  takeReadyAttachments,
  resolveFrontendAccess,
  armFrontendTools,
  resetFrontendTools,
  resetApprovalOutcome,
  setActionError,
  setThreadError,
  applyThread,
  drive,
}: {
  locale: "sv" | "en";
  activeIdRef: MutableRef<string | null>;
  busyRef: MutableRef<boolean>;
  threadRef: MutableRef<ThreadState>;
  frontendContextRef: MutableRef<object | null | undefined>;
  ensureConversation: () => Promise<string | null>;
  hasUploadsInFlight: () => boolean;
  takeReadyAttachments: () => {
    sent: IntelligenceAttachmentRef[];
    attachmentIds: string[];
  };
  resolveFrontendAccess: () => Promise<AccessAnswer>;
  armFrontendTools: (access: AccessAnswer) => object[];
  resetFrontendTools: () => void;
  resetApprovalOutcome: () => void;
  setActionError: StateSetter<ChatCallError | null>;
  setThreadError: StateSetter<ChatCallError | null>;
  applyThread: ApplyThread;
  drive: DriveStream;
}) {
  const send = useCallback(
    async (text: string): Promise<TurnOutcome | null> => {
      const content = text.trim();
      if (!content || busyRef.current) return null;
      setActionError(null);
      resetApprovalOutcome();
      setThreadError(null);

      if (hasUploadsInFlight()) return null;

      const opened = activeIdRef.current;
      const [id, access] = await Promise.all([ensureConversation(), resolveFrontendAccess()]);
      if (!id || activeIdRef.current !== id) return null;
      const fresh = opened ? threadRef.current.nodes.length === 0 : true;
      const declared = armFrontendTools(access);
      const { sent, attachmentIds } = takeReadyAttachments();
      applyThread((state) => beginTurn({ ...state, threadId: id }, content, sent));
      if (fresh) void renameConversation(id, titleFromFirstMessage(content));
      await drive(id, (signal) =>
        openSendStream(
          id,
          {
            content,
            locale,
            frontendContext: frontendContextRef.current ?? null,
            tools: declared,
            attachmentIds,
          },
          signal,
        ),
      );
      return threadRef.current.outcome;
    },
    [
      activeIdRef,
      applyThread,
      armFrontendTools,
      busyRef,
      drive,
      ensureConversation,
      frontendContextRef,
      hasUploadsInFlight,
      locale,
      resetApprovalOutcome,
      resolveFrontendAccess,
      setActionError,
      setThreadError,
      takeReadyAttachments,
      threadRef,
    ],
  );

  const stop = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id) return;
    const result = await cancelTurn(id);
    if (!result.ok) setActionError(result.error);
  }, [activeIdRef, setActionError]);

  const regenerate = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id || busyRef.current) return;
    const last = [...threadRef.current.nodes].reverse().find((node) => node.role === "assistant");
    if (!last) return;
    setActionError(null);
    resetApprovalOutcome();
    resetFrontendTools();
    applyThread(beginRegenerate);
    await drive(id, (signal) => openRegenerateStream(id, last.id, signal));
  }, [
    activeIdRef,
    applyThread,
    busyRef,
    drive,
    resetApprovalOutcome,
    resetFrontendTools,
    setActionError,
    threadRef,
  ]);

  const reconnect = useCallback(async () => {
    const id = activeIdRef.current;
    const state = threadRef.current;
    const runId = state.runId;
    if (!id || !runId || busyRef.current) return;
    await drive(id, (signal) => openRunStream(id, runId, state.lastEventId, signal));
  }, [activeIdRef, busyRef, drive, threadRef]);

  return { send, stop, regenerate, reconnect };
}
